import { forwardRef, useId, useMemo, type ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';
import { formatProductOptions, useCurrencyFormatter } from '~/utils/formatters';
import ProductImage from './ProductImage';

function FormattedPrice({ price }: { price: number }) {
  const currencyFormatter = useCurrencyFormatter();
  return <>{currencyFormatter(price)}</>;
}

function PriceDisplay({
  price,
  className,
}: {
  price: number | string;
  className?: string;
}) {
  return (
    <span className={className}>
      {typeof price === 'number' ? <FormattedPrice price={price} /> : price}
    </span>
  );
}

type Props = {
  name: string;
  options?: { value: string }[];
  imgSrc?: string | null;
  /* Any `price` that's a `string` will be shown as-is and any `number` will be formatted as currency. */
  price?: string | number;
  footnote?: ReactNode;
  quantity?: number;
  remainingReturnableQuantity?: number;
  variant?: 'regular' | 'small';
  children?: ReactNode;
  className?: string;
};

const LineItem = forwardRef<HTMLElement, Props>(
  (
    {
      name,
      imgSrc,
      price,
      quantity,
      remainingReturnableQuantity,
      footnote,
      children,
      className,
      options = [],
      variant = 'regular',
    },
    ref,
  ) => {
    const labelId = useId();

    const description = useMemo(() => formatProductOptions(options), [options]);

    const showPrice = (typeof price === 'number' && price > 0) || !!price;

    return (
      <article
        className={twMerge(
          'flex flex-col gap-8 sm:flex-row sm:items-center',
          className,
        )}
        aria-labelledby={labelId}
        ref={ref}
      >
        <div
          className={twMerge(
            'flex w-full items-center gap-4',
            children && 'sm:basis-2/3',
          )}
        >
          <ProductImage
            expandable
            src={imgSrc}
            alt={name}
            quantity={quantity}
            remainingReturnableQuantity={remainingReturnableQuantity}
            variant={variant}
          />
          <div className="flex w-full flex-col gap-1 label-lg text-on-surface-variant">
            <div className="flex w-full justify-between text-on-surface">
              <span id={labelId}>{name}</span>
              {variant === 'small' && showPrice && (
                <PriceDisplay price={price} className="hidden sm:block" />
              )}
            </div>

            <span className="body-md">{description}</span>
            {variant === 'regular' && showPrice && (
              <PriceDisplay price={price} />
            )}
            {footnote}
          </div>
        </div>
        {children && <div className="sm:basis-1/3">{children}</div>}
      </article>
    );
  },
);

export default LineItem;
